export class TestimonialCarousel {
  private container: HTMLElement;
  private wrapper: HTMLElement;
  private slides: HTMLElement[];
  private pagination: HTMLElement[];
  private currentSlide: number = 0;
  private totalSlides: number;
  private slideWidth: number = 320;
  private autoPlayInterval: number | null = null;
  private isAutoPlaying: boolean = true;

  constructor(containerId: string) {
    this.container = document.getElementById(containerId) as HTMLElement;
    if (!this.container) {
      console.error(`Carousel container with id "${containerId}" not found`);
      return;
    }

    this.wrapper = this.container.querySelector('.swiper-wrapper') as HTMLElement;
    this.slides = Array.from(this.container.querySelectorAll('.swiper-slide')) as HTMLElement[];
    this.pagination = Array.from(this.container.querySelectorAll('.swiper-pagination-bullet')) as HTMLElement[];
    this.totalSlides = this.slides.length;

    this.init();
  }

  private init(): void {
    this.setupEventListeners();
    this.updateSlide();
    this.startAutoPlay();
  }

  private setupEventListeners(): void {
    // Pagination clicks
    this.pagination.forEach((bullet, index) => {
      bullet.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    // Touch/swipe support
    let startX = 0;
    let endX = 0;

    this.container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    });

    this.container.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      this.handleSwipe(startX, endX);
    });

    // Mouse drag support
    let isDragging = false;
    let startPos = 0;

    this.container.addEventListener('mousedown', (e) => {
      isDragging = true;
      startPos = e.clientX;
      this.pauseAutoPlay();
    });

    this.container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      e.preventDefault();
    });

    this.container.addEventListener('mouseup', (e) => {
      if (!isDragging) return;
      isDragging = false;
      const endPos = e.clientX;
      this.handleSwipe(startPos, endPos);
      this.resumeAutoPlay();
    });

    // Pause auto-play on hover
    this.container.addEventListener('mouseenter', () => {
      this.pauseAutoPlay();
    });

    this.container.addEventListener('mouseleave', () => {
      this.resumeAutoPlay();
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
  }

  private handleSwipe(startX: number, endX: number): void {
    const threshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
  }

  private goToSlide(index: number): void {
    this.currentSlide = index;
    this.updateSlide();
    this.updatePagination();
  }

  private nextSlide(): void {
    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    this.updateSlide();
    this.updatePagination();
  }

  private prevSlide(): void {
    this.currentSlide = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
    this.updateSlide();
    this.updatePagination();
  }

  private updateSlide(): void {
    const translateX = -this.currentSlide * this.slideWidth;
    this.wrapper.style.transform = `translateX(${translateX}px)`;

    // Update slide states
    this.slides.forEach((slide, index) => {
      slide.style.opacity = '0.6';
      slide.style.transform = 'scale(0.95)';
      slide.style.zIndex = '1';

      if (index === this.currentSlide) {
        slide.style.opacity = '1';
        slide.style.transform = 'scale(1.05)';
        slide.style.zIndex = '2';
      } else if (index === this.currentSlide - 1 || index === this.currentSlide + 1) {
        slide.style.opacity = '0.8';
        slide.style.transform = 'scale(0.98)';
      }
    });
  }

  private updatePagination(): void {
    this.pagination.forEach((bullet, index) => {
      if (index === this.currentSlide) {
        bullet.classList.add('swiper-pagination-bullet-active');
        bullet.style.width = '8px';
        bullet.style.height = '8px';
        bullet.style.backgroundColor = '#5560C0';
      } else {
        bullet.classList.remove('swiper-pagination-bullet-active');
        bullet.style.width = '4px';
        bullet.style.height = '4px';
        bullet.style.backgroundColor = '#D9D9D9';
      }
    });
  }

  private startAutoPlay(): void {
    if (this.isAutoPlaying) {
      this.autoPlayInterval = window.setInterval(() => {
        this.nextSlide();
      }, 5000); // Change slide every 5 seconds
    }
  }

  private pauseAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  private resumeAutoPlay(): void {
    if (this.isAutoPlaying && !this.autoPlayInterval) {
      this.startAutoPlay();
    }
  }

  public destroy(): void {
    this.pauseAutoPlay();
    // Remove event listeners if needed
  }
}

// Initialize carousel when DOM is loaded
export function initTestimonialCarousel(): void {
  document.addEventListener('DOMContentLoaded', () => {
    const carouselContainer = document.querySelector('.testimonial-swiper');
    if (carouselContainer) {
      new TestimonialCarousel('testimonial-carousel');
    }
  });
}

// Auto-initialize if this module is imported
initTestimonialCarousel(); 